import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import { KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, TASK_MANAGEMENT_PERMISSIONS } from 'constants/general';
import { SECTION_TYPE, STATUS_SECTIONS } from 'constants/materialAssessment';
import useErrorMessage from 'hooks/useErrorMessage';
import useNavigateStakeholders from 'hooks/useNavigateStakeholders';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import i18n from 'i18n';
import { useUpdateSectionMAMutation } from 'store/slices/layoutsMA/apis/materialAssessmentApi';
import {
  useDeleteMaterialityStakeholderMutation,
  useGetMaterialitySHNgosQuery,
  useGetSingleMAInitiateQuery,
} from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import MaterialityAssessmentEmployeesView from './MaterialityAssessmentNGOView';

import type IMaterialSHNgosItem from 'store/slices/materialAssessment/interfaces/IMaterialSHNgosItem';

function MaterialityAssessmentMedia() {
  const navigate = useNavigate();
  const { recordId = '', vcmId = 0 } = useParams();
  const location = useLocation();
  const [openModal, setOpenModal] = useState(false);
  const [itemNGO, setItemNGO] = useState<IMaterialSHNgosItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [currentId, setCurrentId] = useState<number>(-1);
  const [selectedItem, setSelectedItem] = useState<IMaterialSHNgosItem | null>(null);

  const [deleteEmployees] = useDeleteMaterialityStakeholderMutation();
  const { navigateToStakeholder } = useNavigateStakeholders(recordId, Number(vcmId));
  const [updateSection] = useUpdateSectionMAMutation();
  const { data: singleMA, isFetching: isLoadingSingleMA } = useGetSingleMAInitiateQuery({ id: recordId });
  const { data: userData, isLoading: isUserLoading } = useUserMeQuery();
  const {
    access,
  } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });

  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;
  const { data, isFetching: isMaterialityListLoading, error: fetchError } = useGetMaterialitySHNgosQuery({
    offset,
    maId: recordId,
    vcmId: Number(vcmId),
  }, {
    skip: !vcmId,
    refetchOnMountOrArgChange: true,
  });
  const errorMessage = useErrorMessage(fetchError);

  useEffect(() => {
    if (errorMessage && errorMessage !== '') {
      toast.error(i18n.t('notifications.errorHandlerTextMessage', { message: errorMessage }));
    }
  }, [errorMessage]);

  const { meta, items } = data?.data || {};

  const hasMore = meta?.hasNext || false;

  const onLoadMore = () => {
    if (hasMore) {
      setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  useEffect(() => {
    if (items) {
      setItemNGO((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const newItems = items.filter((item) => !existingIds.has(item.id));
        return [...prev, ...newItems];
      });
    }
  }, [items]);

  const handleUpdateSection = () => {
    updateSection({
      maId: recordId,
      type: SECTION_TYPE.STAKEHOLDER_NGOS,
      valueChainMapId: Number(vcmId),
      data: {
        isActive: true,
        status: itemNGO.length > 0 ? STATUS_SECTIONS.COMPLETED : STATUS_SECTIONS.IN_PROGRESS,
      },
    });
  };

  const goBack = () => {
    navigate(AppRoutes.materialityAssessmentStakeholders.replace(':recordId', recordId));
  };

  const goToNextMaterialityAssessment = () => {
    // If VIEW_ONLY, just navigate without updating
    if (isViewOnly) {
      navigateToStakeholder(location.pathname.split('/')[3]);
      return;
    }
    handleUpdateSection();
    navigateToStakeholder(location.pathname.split('/')[3]);
  };

  const onOpenModal = () => {
    if (isViewOnly) {
      return;
    }
    setOpenModal(!openModal);
    if (currentId !== -1 && openModal) {
      setCurrentId(-1);
      setSelectedItem(null);
    }
  };

  const onEditRecord = (id: number) => {
    if (isViewOnly) {
      return;
    }
    onOpenModal();
    setCurrentId(id);
    setSelectedItem(itemNGO.find((item) => item.id === id) || null);
  };

  const onDeleteItem = (id: number) => {
    if (isViewOnly) {
      return;
    }
    deleteEmployees({ id, maId: recordId }).then(() => {
      toast.success(i18n.t('notifications.deletedNGO'));
      setItemNGO((prev) => {
        const filtered = prev.filter((item) => item.id !== id);
        if (filtered.length === 0) {
          updateSection({
            maId: recordId,
            type: SECTION_TYPE.STAKEHOLDER_NGOS,
            valueChainMapId: Number(vcmId),
            data: {
              isActive: true,
              status: STATUS_SECTIONS.IN_PROGRESS,
            },
          });
        }
        return filtered;
      });
    }).catch(() => {
      toast.error(i18n.t('notifications.errorDeletingNGO'));
    });
  };

  const loading = isMaterialityListLoading || isLoadingSingleMA || isUserLoading;
  const vcmName = singleMA?.data?.valueChainMaps?.find((item) => item.id === Number(vcmId))?.name;

  return (
    <MaterialityAssessmentEmployeesView
      isEmptyList={itemNGO.length === 0}
      loading={loading}
      openModal={openModal}
      addStakeholder={onOpenModal}
      goBack={goBack}
      goToNextMaterialityAssessment={goToNextMaterialityAssessment}
      itemNgo={itemNGO}
      onEditRecord={onEditRecord}
      onLoadMore={onLoadMore}
      hasMore={hasMore}
      onDeleteItem={onDeleteItem}
      selectedItem={selectedItem}
      recordId={recordId}
      currentId={currentId}
      setOpenModal={setOpenModal}
      setItemNGO={setItemNGO}
      vcmId={Number(vcmId)}
      vcmName={vcmName || ''}
      isViewOnly={isViewOnly}
    />
  );
}
export default MaterialityAssessmentMedia;
